import React, { useState } from "react";
import {
  Alert,
  Button,
  TextField,
  Box,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import EmailIcon from "@mui/icons-material/Email";
import MailLockIcon from "@mui/icons-material/MailLock";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import BaseDialog from "../../utils/dialog/BaseDialog";

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: "8px 16px",
  textTransform: "none",
  fontWeight: 500,
  minWidth: 100,
}));

const roles = ["viewer", "editor", "admin"];

const INVITE_TYPE_HELP = {
  sso: "This member registers and signs in with a Microsoft work account. Access follows your organization's Microsoft Entra ID, including whether the account is enabled and which sign-in policies apply.",
  local:
    "This member registers and signs in with an email and password in Tax Savy. The account is not managed from your Microsoft Entra admin center. Disabling or removing the person in Entra does not suspend this Tax Savy sign-in. Remove the member in User Management when access should end.",
};

const InviteTypeHelp = ({ label, help }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
    <Typography component='span' variant='body2'>
      {label}
    </Typography>
    <Tooltip title={help} arrow enterTouchDelay={0} placement='top'>
      <IconButton
        size='small'
        aria-label={`About ${label}`}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        sx={{ color: "text.secondary", p: 0.25 }}
      >
        <HelpOutlineIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  </Box>
);

const AddUserDialog = ({ open, onClose, onSubmit, allowLocalInvite = false }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    confirmEmail: "",
    role: "editor",
    inviteType: "sso",
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!emailPattern.test(formData.email))
      newErrors.email = "Invalid email address";

    if (!formData.confirmEmail.trim())
      newErrors.confirmEmail = "Confirm your email";
    else if (formData.email !== formData.confirmEmail)
      newErrors.confirmEmail = "Emails do not match";

    if (!formData.role) newErrors.role = "Role is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit({
        name: formData.name,
        email: formData.email,
        role: formData.role,
        inviteType: allowLocalInvite ? formData.inviteType : "sso",
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      email: "",
      confirmEmail: "",
      role: "editor",
      inviteType: "sso",
    });
    setErrors({});
    onClose();
  };

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      title='Add New User'
      subtitle={
        allowLocalInvite
          ? "Choose how this team member will sign in, then assign a role."
          : "Create and assign a role for a new team member."
      }
      icon={<PersonAddIcon />}
      maxWidth='sm'
      actions={
        <>
          <ActionButton
            onClick={handleClose}
            variant='outlined'
            color='primary'
            sx={{
              borderColor: "grey.400",
              color: "grey.700",
              "&:hover": {
                borderColor: "grey.600",
                backgroundColor: "grey.100",
              },
            }}
          >
            Cancel
          </ActionButton>
          <ActionButton
            onClick={handleSubmit}
            variant='contained'
            color='primary'
          >
            Add User
          </ActionButton>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
        {allowLocalInvite && (
          <FormControl>
            <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
              Sign-in method
            </Typography>
            <RadioGroup
              value={formData.inviteType}
              onChange={(e) =>
                setFormData({ ...formData, inviteType: e.target.value })
              }
            >
              <FormControlLabel
                value='sso'
                control={<Radio size='small' />}
                label={
                  <InviteTypeHelp
                    label='Microsoft sign-in'
                    help={INVITE_TYPE_HELP.sso}
                  />
                }
              />
              <FormControlLabel
                value='local'
                control={<Radio size='small' />}
                label={
                  <InviteTypeHelp
                    label='Email and password'
                    help={INVITE_TYPE_HELP.local}
                  />
                }
              />
            </RadioGroup>
            {formData.inviteType === "local" && (
              <Alert severity='warning' sx={{ mt: 1 }}>
                This sign-in is held in Tax Savy and is not managed from your
                Microsoft Entra admin center. Disabling or removing the person
                in Entra will not revoke their Tax Savy access.
              </Alert>
            )}
          </FormControl>
        )}

        <TextField
          autoFocus
          label='User Name'
          fullWidth
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          error={!!errors.name}
          helperText={errors.name}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <AccountCircleIcon sx={{ color: "action.active" }} />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 8 } }}
        />

        <TextField
          label='User Email'
          type='email'
          fullWidth
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          error={!!errors.email}
          helperText={errors.email}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <EmailIcon sx={{ color: "action.active" }} />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 8 } }}
        />

        <TextField
          label='Confirm Email'
          type='email'
          fullWidth
          value={formData.confirmEmail}
          onChange={(e) =>
            setFormData({ ...formData, confirmEmail: e.target.value })
          }
          error={!!errors.confirmEmail}
          helperText={errors.confirmEmail}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <MailLockIcon sx={{ color: "action.active" }} />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 8 } }}
        />

        <FormControl fullWidth error={!!errors.role}>
          <InputLabel id='role-label'>Role</InputLabel>
          <Select
            labelId='role-label'
            value={formData.role}
            label='Role'
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            sx={{ borderRadius: 8 }}
          >
            {roles.map((r) => (
              <MenuItem key={r} value={r}>
                {r.charAt(0).toUpperCase() + r.slice(1)}
              </MenuItem>
            ))}
          </Select>
          {errors.role && (
            <Typography
              variant='caption'
              color='error'
              sx={{ mt: 0.5, ml: 1.75 }}
            >
              {errors.role}
            </Typography>
          )}
        </FormControl>
      </Box>
    </BaseDialog>
  );
};

export default AddUserDialog;
